// =============================================================================
// src/features/explore/explore-helpers.ts — Helpers puros da feature Explorar.
//
// Proveniencia: assets/explore.js `prm` (linha 25), `getZonaKey` (linha 196),
//   acesso `f.properties.id` (linha 154) e `(m.th||{})[colorBy]` (linha 278).
//   Extraidos como funcoes puras greppaveis para manter o controller curto e
//   testavel sem Leaflet/DOM.
//
// SRP: so resolucao de chave/feature/nivel tematico. Sem efeito colateral.
// =============================================================================

import type {
  ZonaKey,
  MunicipalityId,
  ExploreMunicipality,
  ExploreThematicAxis,
  ElectoralYear,
  PartySigla,
} from '../../app/types';
import { MANAUS_ZONA_KEY_MAP, EXPLORE_DATASET } from '../../data';

/** Feature de municipio do GeoJSON do AM (subset consumido). */
type MunFeatureLike = { properties: { id: MunicipalityId } };
/** Feature de bairro de Manaus (subset consumido). */
type BairroFeatureLike = { properties: { ZONAS: string } };

/** True quando o usuario pediu menos animacao (prefers-reduced-motion). Espelha `prm()`. */
export function prefersReducedMotion(): boolean {
  return window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
}

/** Id IBGE de uma feature de municipio (assets/explore.js usa f.properties.id direto). */
export function munGeoFeatureId(feature: MunFeatureLike): MunicipalityId {
  return feature.properties.id;
}

/** ZonaKey de uma feature de bairro pelo rotulo bruto ZONAS (fallback 'norte'). */
export function bairroZonaKey(feature: BairroFeatureLike): ZonaKey {
  return MANAUS_ZONA_KEY_MAP[feature.properties.ZONAS] ?? 'norte';
}

/**
 * Nivel tematico 0..3 de um municipio num eixo (assets/explore.js `(m.th||{})[cb]`).
 * Ausencia cai em 0, igual ao original (`b == null ? 0 : b`).
 */
export function thematicLevelOf(mun: ExploreMunicipality, axis: ExploreThematicAxis): number {
  return mun.th[axis] ?? 0;
}

/** Faixa de eleitorado (recorte do chip). Pareada com ELEITORADO_BANDS. */
export type EleitoradoBand = 'baixo' | 'medio' | 'alto';

/**
 * Classifica o eleitorado (em milhares) em <10 / 10–50 / >50.
 * diverge do golden por decisao de produto 2026-05-31 (recorte novo).
 */
export function eleitoradoBandOf(eleitoresK: number): EleitoradoBand {
  return eleitoresK > 50 ? 'alto' : eleitoresK >= 10 ? 'medio' : 'baixo';
}

// ── Índices estratégicos derivados (heurísticas sobre a série eleitoral) ───────
// Decisão de produto 2026-06-01 (time de análise): cruzam dados que JÁ existem em
// EXPLORE_DATASET — NÃO são previsão, são leitura do passado p/ priorizar agenda/verba.
// Renomeado "swing" -> "variação" e cortado o rótulo "preditivo" por revisão do auditor.

/** Anos da série eleitoral em ordem cronológica (para volatilidade/variação). */
const ELECTION_YEARS_ASC: readonly ElectoralYear[] = ['2014', '2016', '2018', '2020', '2022', '2024'];

/** Clamp ao intervalo [0,1]. */
function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * Volatilidade partidária 0..5: nº de ciclos em que o partido vencedor MUDOU em
 * relação ao anterior (5 transições em 6 eleições). Alta = terreno infiel/oscilante.
 */
export function volatilidadeOf(mun: ExploreMunicipality): number {
  let trocas = 0;
  let anterior: PartySigla | null = null;
  for (const ano of ELECTION_YEARS_ASC) {
    const resultado = mun.el[ano];
    if (!resultado) continue;
    if (anterior !== null && resultado.p !== anterior) trocas += 1;
    anterior = resultado.p;
  }
  return trocas;
}

/** Variação SINALIZADA do % do vencedor 2022→2024 (sinal = subiu/caiu no card). */
export function variacao2422DeltaOf(mun: ExploreMunicipality): number {
  const atual = mun.el['2024'];
  const anterior = mun.el['2022'];
  if (!atual || !anterior) return 0;
  return atual.pct - anterior.pct;
}

/**
 * Variação absoluta (pp) do % do vencedor entre 2022 e 2024. NÃO é "swing" técnico
 * (pode comparar partidos distintos) — por isso "variação". Renomeado por auditoria.
 */
export function variacao2422Of(mun: ExploreMunicipality): number {
  return Math.abs(variacao2422DeltaOf(mun));
}

/** Eleitorado máximo (Manaus) p/ normalizar o peso — computado uma vez na carga. */
const MAX_ELEITORES_K: number = Math.max(
  1,
  ...Object.values(EXPLORE_DATASET.munis).map((m) => m.eleitoresK),
);

/** Contribuições ponderadas do índice "campo de batalha" (somam o índice 0..1). */
export interface CampoBatalhaParts {
  /** Índice final 0..1 (soma das 3 contribuições). */
  readonly indice: number;
  /** Contribuição da disputa (0..0.45). */
  readonly cDisputa: number;
  /** Contribuição do peso eleitoral (0..0.35). */
  readonly cPeso: number;
  /** Contribuição da volatilidade (0..0.20). */
  readonly cVolatilidade: number;
}

/**
 * Decompõe o "campo de batalha" (prioridade) nas 3 contribuições ponderadas:
 * 0.45·disputa + 0.35·√(peso eleitoral) + 0.20·volatilidade. Heurística do passado —
 * a decomposição é exposta no card de drill-in (exigência do auditor: não parecer ciência).
 */
export function campoBatalhaPartsOf(mun: ExploreMunicipality): CampoBatalhaParts {
  const pct = mun.el['2024']?.pct ?? 50;
  const disputa = clamp01((60 - pct) / 20);
  const peso = Math.sqrt(mun.eleitoresK / MAX_ELEITORES_K);
  const volatilidade = volatilidadeOf(mun) / 5;
  const cDisputa = 0.45 * disputa;
  const cPeso = 0.35 * peso;
  const cVolatilidade = 0.20 * volatilidade;
  return { indice: cDisputa + cPeso + cVolatilidade, cDisputa, cPeso, cVolatilidade };
}

/** Índice "campo de batalha" 0..1 (heurística de prioridade de agenda/verba). */
export function campoBatalhaOf(mun: ExploreMunicipality): number {
  return campoBatalhaPartsOf(mun).indice;
}
